import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiArrowLeft, FiPlus } from "react-icons/fi";
import { GoGraph, GoTable, GoCalendar, GoSync } from "react-icons/go";
import { toast } from "react-toastify";
import WeekReport from "./WeekReport";
import MonthReport from "./MonthReport";
import ResumoLancamentos from "./ResumoLancamentos";
import ImpostosTab from "./ImpostosTab";
import NovaSemanaModal from "../modals/NovaSemanaModal";
import { computarApuracao } from "../utils/apuracaoSemanal";
import { SEMANAS_MOCK, rangesSemanais } from "../mockDataSemanal";
import { num } from "../utils/formatters";
import plcomService from "../../../services/plcomService";

const MESES = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

const MAX_SEMANAS = 4;

function competenciaLabel(iso) {
  if (!iso) return "";
  const [y, m] = iso.split("-").map(Number);
  return `${MESES[m - 1]}/${y}`;
}

/**
 * Detalhe de um relatório de apuração — navegação lateral estilo iOS
 * Settings: lista de períodos à esquerda (Semana 1..4 + "Novo relatório
 * semanal" + seção Consolidado), relatório da seleção à direita.
 */
export default function RelatorioDetalhe({ relatorio, onVoltar, onUpdate }) {
  const [semanas, setSemanas] = useState(relatorio.semanas);
  // 0..n-1 semanas | n = mês | n+1 = resumo | n+2 = impostos
  const [aba, setAba] = useState(0);
  const [lancamentos, setLancamentos] = useState({}); // {weekIdx: [...]}
  const [obsMap, setObsMap] = useState({}); // {weekIdx: string}
  const [novaSemanaOpen, setNovaSemanaOpen] = useState(false);
  const [processando, setProcessando] = useState(relatorio.status === "processando");
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const isApi = !!relatorio.api;

  // Relatórios da API: carrega o doc completo (semanas + manual + obs) e
  // faz polling enquanto o worker ainda estiver processando os XMLs.
  useEffect(() => {
    if (!isApi) return;
    let ativo = true;
    plcomService.detalhar(relatorio.id)
      .then(async (doc) => {
        if (!ativo || !mountedRef.current) return;
        if (doc.status === "processando") {
          setProcessando(true);
          doc = await plcomService.aguardar(relatorio.id);
          if (!ativo || !mountedRef.current) return;
          setProcessando(false);
        }
        setSemanas(doc.semanas || []);
        setLancamentos(doc.manual || {});
        setObsMap(doc.obs || {});
        if (doc.status === "erro") toast.error(`Apuração falhou: ${doc.erro || "erro desconhecido"}`);
        if (doc.avisos?.length) toast.info(`${doc.avisos.length} aviso(s) na apuração (notas ignoradas ou CST fora da regra).`);
        onUpdate?.({ semanas: doc.semanas || [], status: doc.status, xmls: doc.xmlCount });
      })
      .catch(() => setProcessando(false));
    return () => { ativo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relatorio.id]);

  const competencia = relatorio.competencia;
  const ranges = useMemo(() => rangesSemanais(competencia), [competencia]);

  const { periodos, mes } = useMemo(
    () => computarApuracao(semanas, semanas.map((_, i) => lancamentos[i] || [])),
    [semanas, lancamentos]
  );

  const nomes = semanas.map((_, i) => `Semana ${i + 1}`);
  const abaMes = semanas.length;
  const abaResumo = semanas.length + 1;
  const abaImpostos = semanas.length + 2;
  const temManual = Object.values(lancamentos).some((l) => l && l.length > 0);
  const completo = semanas.length >= MAX_SEMANAS;

  const gerarSemana = async (files) => {
    const idx = semanas.length;
    if (idx >= MAX_SEMANAS) return;

    if (isApi) {
      // upload real: o worker reagrega e devolve todas as semanas
      try {
        setProcessando(true);
        await plcomService.uploadXmls({
          files, relatorioId: relatorio.id, semanaAlvo: idx + 1,
        });
        const doc = await plcomService.aguardar(relatorio.id);
        if (!mountedRef.current) return;
        setProcessando(false);
        if (doc.status === "erro") {
          toast.error(`Apuração falhou: ${doc.erro || "erro desconhecido"}`);
          return;
        }
        setSemanas(doc.semanas || []);
        onUpdate?.({ semanas: doc.semanas || [], status: doc.status, xmls: doc.xmlCount });
        setAba((doc.semanas || []).length - 1);
      } catch (e) {
        setProcessando(false);
        toast.error(e.response?.data?.error || "Falha ao enviar XMLs da semana.");
      }
      return;
    }

    const novas = [...semanas, SEMANAS_MOCK[idx]];
    setSemanas(novas);
    onUpdate?.({
      semanas: novas,
      status: novas.length >= MAX_SEMANAS ? "concluido" : "em_andamento",
    });
    setAba(idx); // abre a semana recém-gerada
  };

  // persistência dos lançamentos manuais/obs no doc do relatório (API)
  const persistir = (manual, obs) => {
    if (!isApi) return;
    plcomService.atualizar(relatorio.id, { manual, obs }).catch(() => {});
  };

  const addLanc = (weekIdx, lanc) =>
    setLancamentos((prev) => {
      const next = { ...prev, [weekIdx]: [...(prev[weekIdx] || []), lanc] };
      persistir(next, obsMap);
      return next;
    });

  const removeLanc = (weekIdx, id) =>
    setLancamentos((prev) => {
      const next = { ...prev, [weekIdx]: (prev[weekIdx] || []).filter((l) => l.id !== id) };
      persistir(next, obsMap);
      return next;
    });

  const setObs = (weekIdx, v) =>
    setObsMap((prev) => {
      const next = { ...prev, [weekIdx]: v };
      persistir(lancamentos, next);
      return next;
    });

  const viewTabs = [
    { id: "semanas", label: "Relatório semanal", icon: GoCalendar },
    { id: abaMes, label: "Mês consolidado", icon: GoGraph },
    { id: abaResumo, label: "Resumo dos lançamentos", icon: GoTable },
    { id: abaImpostos, label: "Impostos", icon: GoTable },
  ];

  // Cards de semana visíveis na aba "Relatório semanal" (semana aberta)
  // ou quando ainda não há nenhuma semana (só o card pontilhado)
  const mostrarCards = semanas.length === 0 || aba < abaMes;
  const semanaAtiva = aba < abaMes && periodos[aba];

  return (
    <>
      {/* ===== Header do detalhe ===== */}
      <div className="detail-head">
        <button type="button" className="btn-ghost" onClick={onVoltar}>
          <FiArrowLeft /> Relatórios
        </button>
        <div className="detail-title">
          <span className="eyebrow">Apuração semanal de tributos · notas de {competenciaLabel(competencia).toLowerCase()}</span>
          <h4>{competenciaLabel(competencia)}</h4>
        </div>
        {processando && (
          <span className="pill processing"><GoSync className="spin" /> Processando XMLs…</span>
        )}
        <span className="pill">Valores em R$</span>
      </div>

      {/* ===== Visões consolidadas — diretamente acima dos cards de semana ===== */}
      {semanas.length > 0 && (
        <div className="tabs-header detail-tabs">
          {viewTabs.map((t) => {
            const Icon = t.icon;
            const ativo = t.id === "semanas" ? aba < abaMes : aba === t.id;
            return (
              <button
                key={t.id}
                type="button"
                className={`tab ${ativo ? "active" : ""}`}
                onClick={() => setAba(t.id === "semanas" ? 0 : t.id)}
              >
                <Icon />
                {t.label}
              </button>
            );
          })}
        </div>
      )}

      {/* ===== Cards de semana — visíveis na aba Semanas ou com semana aberta ===== */}
      {mostrarCards && (
        <div className="week-cards">
          {semanas.map((s, i) => {
            const p = periodos[i];
            const credor = p.totalC < -0.005;
            return (
              <button
                key={i}
                type="button"
                className={`week-card ${aba === i ? "selected" : ""} ${credor ? "credor" : ""}`}
                onClick={() => setAba(i)}
              >
                <span className="wk-head">
                  <span className="wk-badge">{i + 1}</span>
                  <span className="wk-mid">
                    <span className="wk-name">Semana {i + 1}</span>
                    <span className="wk-range">{ranges[i]}</span>
                  </span>
                </span>
                <span className="wk-value">
                  <b>{num(Math.abs(p.totalC))}</b>
                  <small>{credor ? "credor" : "a recolher"}</small>
                </span>
              </button>
            );
          })}

          {!completo && (
            <button
              type="button"
              className="week-card is-dotted"
              onClick={() => setNovaSemanaOpen(true)}
              disabled={processando}
            >
              <FiPlus />
              <span className="wk-name">Novo relatório semanal</span>
              <span className="wk-range">{ranges[semanas.length]}</span>
            </button>
          )}
        </div>
      )}


      {/* ===== Conteúdo da seleção ===== */}
      <div className="detail-content">
          {semanas.length === 0 ? (
            <div className="info-banner">
              <div className="banner-icon"><GoCalendar /></div>
              <div className="banner-text">
                <strong>Nenhuma semana gerada ainda.</strong>
                <span> Toque em "Novo relatório semanal" para subir os XMLs e gerar o primeiro demonstrativo.</span>
              </div>
            </div>
          ) : (
            <>
              {semanaAtiva && (
                <WeekReport
                  w={semanas[aba]}
                  p={periodos[aba]}
                  nome={nomes[aba]}
                  range={ranges[aba]}
                  lancamentos={lancamentos[aba] || []}
                  obs={obsMap[aba] || ""}
                  onAddLanc={(l) => addLanc(aba, l)}
                  onRemoveLanc={(id) => removeLanc(aba, id)}
                  onObs={(v) => setObs(aba, v)}
                />
              )}
              {aba === abaMes && (
                <MonthReport
                  mes={mes}
                  periodos={periodos}
                  nomes={nomes}
                  ranges={ranges}
                  temManual={temManual}
                />
              )}
              {aba === abaResumo && (
                <ResumoLancamentos
                  mes={mes}
                  periodos={periodos}
                  nomes={nomes}
                  ranges={ranges}
                />
              )}
              {aba === abaImpostos && <ImpostosTab />}

              <p className="apura-footer">
                {isApi
                  ? "Fonte: apuração a partir dos XMLs enviados (NF-e modelo 55 e CT-e). "
                  : "Fonte: protótipo de apuração semanal da PLcom (dados mock). "}
                {aba === abaResumo
                  ? "Grades de apoio do mês consolidado."
                  : aba === abaMes
                    ? "Mês consolidado = soma das semanas por data de emissão, dos lançamentos manuais e dos lançamentos do mês."
                    : aba === abaImpostos
                      ? "Espelho fiel da aba IMPOSTOS da planilha de movimentações."
                      : "Semana por data de emissão da nota (entradas pela data de recebimento)."}
              </p>
            </>
          )}
        </div>

      <NovaSemanaModal
        isOpen={novaSemanaOpen}
        onClose={() => setNovaSemanaOpen(false)}
        semanaNumero={semanas.length + 1}
        range={ranges[semanas.length]}
        onGerar={gerarSemana}
      />
    </>
  );
}
